import styled from "@emotion/styled";
import type { SlotStatus } from "@clustec/common";
import { theme } from "../lib/theme";
import { orderedStatusCounts, slotStatusColor } from "./SlotStatusLabel";

/**
 * Distribution of consecutive-miss run lengths — how many bursts of each length, coloured by cause.
 *
 * This replaces a row of text pills, one per status, each reading `×3 — 5`. The numbers were all
 * there and told you nothing: the question the section exists to answer is "are misses isolated or
 * correlated", which is the *shape* of this distribution, and nobody sees a shape in fifteen pills
 * of identical size. Worse, the pills grouped by status and then sorted by length, so the two
 * comparisons that matter — one length against another, and one cause against another at the same
 * length — both took arithmetic in your head.
 *
 * As a histogram both are immediate: run length along the x-axis, occurrences as height, cause as
 * the colour of the stack. A wall at ×1 is a flaky-individuals picture; a bump out at ×4 or beyond
 * is something systemic, and its colour says which failure mode.
 */

/**
 * Longest run drawn as its own column. Beyond this the tail pools into one `N+` column: run
 * lengths are unbounded — a network stalled for an hour is one run of fifty — and a single such
 * outlier would otherwise stretch the axis until every column that mattered was a hairline.
 */
const MAX_COLUMNS = 15;

export interface RunColumn {
  /** Run length in slots. For the pooled column, the lower bound of the lengths it covers. */
  length: number;
  /** True for the pooled tail column, which covers `length` slots and everything longer. */
  pooled: boolean;
  /** Runs in this column, by cause. Empty for a length that never occurred. */
  byStatus: Record<string, number>;
  /** Runs in this column, all causes. */
  runs: number;
  /** Slots those runs cost — one ×8 costs what eight ×1s do. */
  slots: number;
}

export interface MissRunSummary {
  /** One per run length from 1 up, including lengths that never occurred. */
  columns: RunColumn[];
  /** The true longest run, which the pooled column's label deliberately doesn't state. */
  longest: number;
  runs: number;
  slots: number;
}

/**
 * Turn `status → run length → occurrences` into one column per length, plus the totals a caption
 * needs.
 *
 * Mined runs are dropped: a run of consecutive *mined* slots is a healthy streak, not a burst, and
 * this section is about misses. Every other status is kept, including any the UI doesn't know
 * about, so a new taxonomy case draws as part of a column instead of silently disappearing.
 */
export function summarizeMissRuns(
  runLengths: Record<string, Record<string, number>>,
): MissRunSummary {
  const cells = new Map<
    number,
    { byStatus: Record<string, number>; runs: number; slots: number }
  >();
  let longest = 0;
  let runs = 0;
  let slots = 0;

  for (const [status, lengths] of Object.entries(runLengths)) {
    if (status === "checkpoint-mined") continue;
    for (const [rawLength, occurrences] of Object.entries(lengths)) {
      const length = Number(rawLength);
      if (!Number.isInteger(length) || length < 1 || occurrences <= 0) continue;
      longest = Math.max(longest, length);
      runs += occurrences;
      slots += length * occurrences;
      const key = Math.min(length, MAX_COLUMNS);
      const cell = cells.get(key) ?? { byStatus: {}, runs: 0, slots: 0 };
      cell.byStatus[status] = (cell.byStatus[status] ?? 0) + occurrences;
      cell.runs += occurrences;
      cell.slots += length * occurrences;
      cells.set(key, cell);
    }
  }

  if (longest === 0) return { columns: [], longest: 0, runs: 0, slots: 0 };

  // Every length from 1 up, including the ones that never happened: a hole at ×2 between a wall at
  // ×1 and a bump at ×3 is a real feature of the distribution, and an axis of only the lengths
  // present would draw those three adjacent as if they were consecutive.
  const upTo = Math.min(longest, MAX_COLUMNS);
  const columns: RunColumn[] = [];
  for (let length = 1; length <= upTo; length++) {
    const cell = cells.get(length);
    columns.push({
      length,
      pooled: length === MAX_COLUMNS && longest > MAX_COLUMNS,
      byStatus: cell?.byStatus ?? {},
      runs: cell?.runs ?? 0,
      slots: cell?.slots ?? 0,
    });
  }
  return { columns, longest, runs, slots };
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const Plot = styled.div`
  position: relative;
  height: 72px;
  border-bottom: 1px solid ${theme.colors.border};
`;

const Gridline = styled.div<{ atPct: number }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: ${(p) => p.atPct}%;
  border-top: 1px dashed ${theme.colors.border};
  opacity: 0.5;
`;

/**
 * The occurrence count at the top of the axis, in the gutter. Only the ceiling is labelled: a
 * count histogram is read off its maximum, and a second tick on a 72px box crowds the columns it
 * would be there to explain.
 */
const TickLabel = styled.span`
  position: absolute;
  right: 0;
  bottom: 100%;
  transform: translateY(50%);
  padding-left: 4px;
  font-family: monospace;
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text};
  background: ${theme.colors.bg};
  line-height: 1;
`;

const Columns = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  gap: 2px;
`;

/**
 * Columns are capped and left-aligned rather than stretched to fill. A window whose misses are all
 * isolated singles has one column, and a full-panel slab reads as a magnitude — the height already
 * carries that, and the count is on the axis.
 */
const COLUMN_MAX_WIDTH = "36px";

/**
 * One run length. Height is its share of the tallest column, with a visible floor: "one run of
 * nine" is routinely a thousandth of the ×1 column and the most alarming number in the section, so
 * it must not round away. A length that never occurred draws nothing at all, which is what keeps
 * the floor honest — a short column means few, never none.
 */
const Column = styled.div<{ h: number }>`
  flex: 1;
  min-width: 4px;
  max-width: ${COLUMN_MAX_WIDTH};
  height: ${(p) => (p.h > 0 ? `max(${p.h}%, 3px)` : "0")};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  border-radius: 2px 2px 0 0;
  overflow: hidden;
`;

const Slice = styled.span<{ pct: number; color: string }>`
  display: block;
  width: 100%;
  height: ${(p) => p.pct}%;
  background: ${(p) => p.color};
`;

/** x-axis, one cell per column so the labels stay under the columns they name. */
const AxisRow = styled.div`
  display: flex;
  gap: 2px;
  font-size: 9px;
  font-family: monospace;
  color: ${theme.colors.textMuted};
`;

const AxisCell = styled.span`
  flex: 1;
  min-width: 4px;
  max-width: ${COLUMN_MAX_WIDTH};
  text-align: center;
  overflow: hidden;
`;

const Caption = styled.div`
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
  line-height: 1.5;
`;

export interface MissRunHistogramProps {
  /** `runLengths` from `production/stats`: status → run length → occurrences. */
  runLengths: Record<string, Record<string, number>>;
}

export function MissRunHistogram({ runLengths }: MissRunHistogramProps) {
  const { columns, longest, runs, slots } = summarizeMissRuns(runLengths);
  if (columns.length === 0) return null;

  const peak = Math.max(...columns.map((c) => c.runs));

  return (
    <Wrapper>
      <Plot>
        <Gridline atPct={50} />
        <Columns>
          {columns.map((c) => {
            const slices = orderedStatusCounts(c.byStatus);
            const label = c.pooled
              ? `${c.length} or more slots`
              : `${c.length} slot${c.length > 1 ? "s" : ""}`;
            return (
              <Column
                key={c.length}
                h={peak > 0 ? (c.runs / peak) * 100 : 0}
                title={
                  c.runs === 0
                    ? `No run of ${label}`
                    : `Runs of ${label} — ${c.runs} ${c.runs === 1 ? "run" : "runs"}, ` +
                      `${c.slots} slots lost\n` +
                      slices.map(([status, count]) => `${status}: ${count}`).join("\n")
                }
              >
                {/* Reversed: the column is normal block flow, so the first child paints at the top
                    while the stack order is bottom-up. */}
                {[...slices].reverse().map(([status, count]) => (
                  <Slice
                    key={status}
                    pct={(count / c.runs) * 100}
                    color={slotStatusColor(status as SlotStatus)}
                  />
                ))}
              </Column>
            );
          })}
        </Columns>
        <TickLabel>{peak}</TickLabel>
      </Plot>
      <AxisRow>
        {columns.map((c) => (
          <AxisCell key={c.length}>{c.pooled ? `${c.length}+` : c.length}</AxisCell>
        ))}
      </AxisRow>
      <Caption>
        x-axis: run length in slots. {runs} {runs === 1 ? "run" : "runs"}, {slots} slots lost,
        longest ×{longest}.
      </Caption>
    </Wrapper>
  );
}
